import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, inputCls, Field, StatusPill } from "@/components/AdminTools";
import { Plus, Trash2, Save, X } from "lucide-react";

const CATEGORIES = ["sleep", "metabolic", "hormone", "diagnostics", "wellness", "other"];
const KINDS = ["service", "product"];
const STATUSES = ["active", "draft", "archived"];
const EMPTY = { name: "", kind: "service", category: "sleep", description: "", wholesale_cost_estimate: "", suggested_retail_min: "", suggested_retail_max: "", status: "active" };

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const r = await api.get("/services");
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (it) => {
    setEditing(it.id);
    setForm({ ...EMPTY, ...it,
      wholesale_cost_estimate: it.wholesale_cost_estimate ?? "",
      suggested_retail_min: it.suggested_retail_min ?? "",
      suggested_retail_max: it.suggested_retail_max ?? "",
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    const payload = {
      ...form,
      wholesale_cost_estimate: form.wholesale_cost_estimate === "" ? null : Number(form.wholesale_cost_estimate),
      suggested_retail_min: form.suggested_retail_min === "" ? null : Number(form.suggested_retail_min),
      suggested_retail_max: form.suggested_retail_max === "" ? null : Number(form.suggested_retail_max),
    };
    try {
      if (editing === "new") {
        await api.post("/services", payload);
        toast.success("Created.");
      } else {
        await api.patch(`/services/${editing}`, payload);
        toast.success("Updated.");
      }
      cancel();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this service?")) return;
    try { await api.delete(`/services/${id}`); load(); toast.success("Deleted."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Catalog"
      title="Services & products"
      intro="The atomic catalog clinics deploy through curated programs. Vendors fulfill, clinics deliver."
      actions={<button data-testid="services-new-btn" onClick={startNew} className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"><Plus className="w-3.5 h-3.5" /> New</button>}
    >
      {editing && (
        <div data-testid="services-form" className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" required><input data-testid="services-form-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className={inputCls} /></Field>
            <Field label="Kind">
              <select value={form.kind} onChange={(e)=>setForm({...form,kind:e.target.value})} className={inputCls}>
                {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Category">
              <select value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} className={inputCls}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})} className={inputCls}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description"><textarea rows={3} value={form.description||""} onChange={(e)=>setForm({...form,description:e.target.value})} className={`${inputCls} resize-y`} /></Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Wholesale est. ($)" hint="From vendor"><input type="number" value={form.wholesale_cost_estimate} onChange={(e)=>setForm({...form,wholesale_cost_estimate:e.target.value})} className={inputCls} /></Field>
            <Field label="Suggested retail min ($)"><input type="number" value={form.suggested_retail_min} onChange={(e)=>setForm({...form,suggested_retail_min:e.target.value})} className={inputCls} /></Field>
            <Field label="Suggested retail max ($)"><input type="number" value={form.suggested_retail_max} onChange={(e)=>setForm({...form,suggested_retail_max:e.target.value})} className={inputCls} /></Field>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <button data-testid="services-form-save" onClick={save} className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"><Save className="w-3.5 h-3.5" /> Save</button>
            <button onClick={cancel} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"><X className="w-3.5 h-3.5" /> Cancel</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Kind · Category</th>
              <th className="text-left px-4 py-3">Pricing range</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((it) => (
              <tr key={it.id} data-testid={`service-row-${it.id}`}>
                <td className="px-4 py-3">
                  <div className="font-display font-semibold text-slate-900">{it.name}</div>
                  <div className="text-[11px] text-slate-500 line-clamp-1">{it.description}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{it.kind} · <span className="capitalize">{it.category}</span></td>
                <td className="px-4 py-3 text-slate-700 tabular">
                  {it.suggested_retail_min != null ? `$${it.suggested_retail_min}` : "—"} – {it.suggested_retail_max != null ? `$${it.suggested_retail_max}` : "—"}
                </td>
                <td className="px-4 py-3"><StatusPill status={it.status} /></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={()=>startEdit(it)} className="text-xs font-semibold text-slate-700 hover:text-slate-900 mr-3">Edit</button>
                  <button onClick={()=>remove(it.id)} className="inline-flex items-center text-xs text-red-600 hover:text-red-800"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-10">No services yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </ToolPageShell>
  );
}
